<script setup lang="ts">
import { isMobile, isTablet } from 'mobile-device-detect';
import LazyImage from './../LazyImage.vue';
import { apiFetchAminityInfo } from '../../api';
import { LotPosition, AmenityInfo, Amenity } from './../../types';
import LeftArrowIcon from '@/assets/icons/LeftArrow.vue';
import lodash from 'lodash';
import {
  addImageScaler,
  calcZoom,
  getPlatMapModalPosition,
  getPlatMapModalPositionMobile,
  getTouchesCenter,
  getZoomDistance,
} from './../../core';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  onUpdated,
  Ref,
  ref,
  toRefs,
  watch,
} from 'vue';
import { Carousel, Navigation, Slide } from 'vue3-carousel';

const props = defineProps<{
  position: LotPosition;
  amenityId: string;
  underlay: boolean;
  amenities: Array<string>;
  allAmenities: Array<Amenity>;
  isKioskMode: boolean;
  zoomLevel: number;
}>();
const {
  position,
  amenityId,
  underlay,
  amenities,
  allAmenities,
  isKioskMode,
  zoomLevel,
} = toRefs(props);
const emits = defineEmits([
  'closeAmenityInfo',
  'updateLotPosition',
  'setAminityId',
  'setMapCenter',
]);

const sliderOptions: any = {
  slidesToShow: 1,
  infinite: false,
};
const amenityTitle = ref('');
const amenityCaption = ref('');
const hasMobile = !!(isMobile && !isTablet);
const hasTablet = isTablet;
const amenityInfo: any = ref({});
const loading = ref(true);
const lotPosition = ref({} as any);
const nextAmenityName = ref('');
const baseElement = ref(null as any);
const slick = ref(null as any);

const zoom = ref(1);
const zoomCenter = ref({
  x: 0,
  y: 0,
});
const lastDistance = ref(0);
const minZoomBounce = ref(0.015);
const maxZoomBounce = ref(0.015);
const width = ref(window.innerWidth);
const height = ref(window.innerHeight);
const lastX = ref(null as any);
const lastY = ref(null as any);
const x = ref(0);
const y = ref(0);
const transition = ref({
  transform: null as any,
  origin: 'transform-origin 400ms linear' as any,
});
const minZoom = ref(1);
const maxZoom = ref(10);
const speed = ref(0.5);

const closeAmenityInfo = () => {
  emits('closeAmenityInfo');
};

const updateLotPosition = (lotPosition: LotPosition) => {
  emits('updateLotPosition', lotPosition);
};
const setAminityId = (amenityId: string) => {
  emits('setAminityId', amenityId);
};
const setMapCenter = (x: number, y: number) => {
  emits('setMapCenter', x, y);
};

watch(amenityId, (val: string, oldVal: string) => {
  if (val !== oldVal) {
    resetZoom();
    fetchAmenityInfo(val);
  }
});

watch(position, (val: LotPosition, oldVal: LotPosition) => {
  if (!lodash.isEqual(val, oldVal)) {
    lotPosition.value = val;
  }
});

onMounted(() => {
  lotPosition.value = position.value;
  fetchAmenityInfo(amenityId.value);
});

const handleReInit = () => {
  nextTick(() => {
    slick.value.reSlick();
  });
};

const beforeAmenityClose = () => {
  amenityInfo.value = {} as AmenityInfo;
  let timer = setTimeout((_) => {
    closeAmenityInfo();
    clearTimeout(timer);
  }, 10);
};

onUpdated(() => {
  nextTick(function () {
    // if (!isMobile && !isTablet)
    updateModalPosition();
  });
});

onUnmounted(() => {
  const el = baseElement.value;
  if (el) {
    el.removeEventListener('touchstart', start);
    el.removeEventListener('touchmove', move);
    el.removeEventListener('touchend', end);
  }
});

const bottomArrowCondition = (xPosition: string, yPosition: string) => {
  return (
    !lodash.isEmpty(lotPosition.value) &&
    !!(
      lotPosition.value.xPosition === xPosition &&
      lotPosition.value.yPosition === yPosition
    )
  );
};

const loadNextAmenityName = () => {
  let currentIndex = amenities.value.indexOf(amenityId.value);
  const nextIndex = ++currentIndex % amenities.value.length;
  const amenityIdData = amenities.value[nextIndex];
  const nextAminity = allAmenities.value.filter((x) => x.id === amenityIdData);
  if (nextAminity.length) nextAmenityName.value = nextAminity[0].title;
  else nextAmenityName.value = '';
};

const loadNewAmenity = () => {
  let currentIndex = amenities.value.indexOf(amenityId.value);
  const nextIndex = ++currentIndex % amenities.value.length;
  const amenityIdData = amenities.value[nextIndex];
  if (underlay.value) {
    movePlatmapWithMap(amenityIdData);
  } else {
    movePlatmapWithoutMap(amenityIdData);
  }
  setAminityId(amenityIdData);
};

const movePlatmapWithoutMap = (amenityId: string) => {
  const amenityElement = document.getElementById(amenityId) as HTMLElement;
  if (amenityElement) {
    const amenityElementPosition = amenityElement.getBoundingClientRect();
    const modalWidth = 364;
    let overlayContainer = document.getElementById('plan-svg') as HTMLElement;
    const containerTop = parseFloat(
      overlayContainer.style.top.replace('px', '')
    );
    const containerLeft = parseFloat(
      overlayContainer.style.left.replace('px', '')
    );
    const topPosition = -(
      amenityElementPosition.y -
      (containerTop + window.innerHeight / 2)
    );
    let leftPosition = -(amenityElementPosition.x - containerLeft);
    if (isMobile) {
      if (leftPosition < window.innerWidth / 2) {
        leftPosition += window.innerWidth / 2;
      }
      if (leftPosition > window.innerWidth) {
        leftPosition = window.innerWidth;
      }
    } else {
      if (leftPosition < window.innerWidth / 2 + modalWidth) {
        leftPosition += window.innerWidth / 2 + modalWidth;
      }
      if (leftPosition > window.innerWidth) {
        leftPosition = window.innerWidth;
      }
    }

    overlayContainer.style.left = `${leftPosition}px`;
    overlayContainer.style.top = `${topPosition}px`;
  }
};

const movePlatmapWithMap = (amenityId: string) => {
  const amenityElement = document.getElementById(amenityId) as HTMLElement;
  if (amenityElement) {
    const amenityElementPosition = amenityElement.getBoundingClientRect();
    setMapCenter(amenityElementPosition.x, amenityElementPosition.y);
  }
};

const updateModalPosition = () => {
  let lotElement = document.getElementById(amenityId.value) as HTMLElement;
  if (lotElement) {
    const modelContainer = document.getElementById(
      'amenity-modal'
    ) as HTMLElement;
    let elementPosition = lotElement.getBoundingClientRect();
    const lotPositionData =
      isMobile || isTablet
        ? getPlatMapModalPositionMobile(elementPosition)
        : getPlatMapModalPosition(
            elementPosition,
            modelContainer.clientHeight,
            zoomLevel.value
          );
    if (!lodash.isEqual(lotPosition.value, lotPositionData)) {
      updateLotPosition(lotPositionData);
    }
  }
};

const resetAmenityInfo = () => {
  amenityTitle.value = '';
  amenityCaption.value = '';
  amenityInfo.value = {} as AmenityInfo;
};

const setAmenityTitleAndCaption = () => {
  for (const [key, value] of Object.entries(amenityInfo.value as AmenityInfo)) {
    if (!amenityTitle.value && value.title) amenityTitle.value = value.title;
    if (!amenityCaption.value && value.caption)
      amenityCaption.value = value.caption;
  }
};

const addScaler = (url) => {
  return addImageScaler(url);
};

const fetchAmenityInfo = async (amenityId: string) => {
  try {
    resetAmenityInfo();
    const data = await apiFetchAminityInfo(amenityId);
    const { result } = data;
    if (result.length) {
      amenityInfo.value = result as AmenityInfo;
      setAmenityTitleAndCaption();
    }
    loadNextAmenityName();
    loading.value = false;
  } catch (error) {
    amenityInfo.value = {} as AmenityInfo;
    loading.value = false;
  }
};

const start = (event) => {
  // event.preventDefault();
  if (event.touches.length === 2) {
    handlePinchStart(event);
  }
};

const handlePinchStart = (event) => {
  const { x, y } = getTouchCenter(
    getTouchesCenter(event.touches[0], event.touches[1])
  );
  if (zoom.value < maxZoom.value) {
    zoomCenter.value.x = x;
    zoomCenter.value.y = y;
  }
  lastDistance.value = getZoomDistance(event.touches[0], event.touches[1]);
};

const getTouchCenter = ({ x, y }) => {
  const rect = baseElement.value.getBoundingClientRect();
  return {
    x: x - rect.left,
    y: y - rect.top,
  };
};

const move = (event) => {
  // event.preventDefault();
  if (event.touches.length === 2) {
    handlePinchMove(event);
  } else {
    handlePanMove(event);
  }
};

const handlePinchMove = (event) => {
  let zoom = getZoomDistance(event.touches[0], event.touches[1]);
  const last = zoom / lastDistance.value;
  const scale = calcZoom(
    minZoom.value - minZoomBounce.value,
    maxZoom.value + maxZoomBounce.value,
    zoom * last
  );
  if (zoom < maxZoom.value || (zoom >= maxZoom.value && scale < zoom)) {
    zoom = scale;
  }
  lastDistance.value = zoom;
};

const handlePanMove = (event) => {
  if (zoom.value > 1) {
    const touch = event.touches[0];
    const x = touch.clientX;
    const y = touch.clientY;
    if (!lastX.value || !lastY.value) {
      lastX.value = x;
      lastY.value = y;
      return null;
    } else {
      const rect = baseElement.value.getBoundingClientRect();
      let diffX = (x - lastX.value) * speed.value;
      let diffY = (y - lastY.value) * speed.value;
      transition.value.transform = null;
      //apply transform
      x.value += diffX;
      y.value += diffY;
      //bounds
      if (rect.left > 0 && diffX > 0) {
        x.value -= diffX;
      }
      if (rect.right < width.value && diffX < 0) {
        x.value -= diffX;
      }
      if (rect.top > 0 && diffY > 0) {
        y.value -= diffY;
      }
      if (rect.bottom < height.value && diffY < 0) {
        y.value -= diffY;
      }
      //to calc the next move
      lastX.value = x;
      lastY.value = y;
    }
  }
};

const end = (event) => {
  // event.preventDefault();
  if (zoom.value <= minZoom.value) {
    resetZoom();
  } else {
    lastX.value = 0;
    lastY.value = 0;
  }
};

const resetZoom = () => {
  zoom.value = 1;
  zoomCenter.value.x = 0;
  zoomCenter.value.y = 0;
  x.value = 0;
  y.value = 0;
};

const zoomStyles = () => {
  let transitionData = '';
  if (transition.value.transform) transitionData += transition.value.transform;
  if (transition.value.origin)
    transitionData +=
      (transitionData.length > 0 ? ', ' : '') + transition.value.origin;
  return {
    transform: `scale(${zoom.value}) translate3d(${x.value}px, ${y.value}px, 0px)`,
    transitionData,
  };
};
</script>

<template>
  <div
    id="amenity-modal"
    class="embinfobox elem-modal basic"
    v-bind:style="[
      !lodash.isEmpty(lotPosition)
        ? {
            top: `${lotPosition.top}px`,
            left: `${
              zoomLevel > 19 || hasMobile
                ? lotPosition.left
                : lotPosition.left + 25
            }px`,
          }
        : {},
      ,
      zoomStyles,
    ]"
    ref="baseElement"
    v-on="
      isKioskMode ? { touchstart: start, touchmove: move, touchend: end } : {}
    "
  >
    <div aria-label="card" class="map-card">
      <i
        aria-label="close icon"
        class="emb_closeicon"
        @click="beforeAmenityClose"
      ></i>
      <div class="emb-card_body" v-if="!loading">
        <div class="mb-10">
          <h3 class="ammenity-title" v-if="amenityTitle">
            {{ amenityTitle }}
          </h3>
          <p class="ammenity-site-address">Community feature</p>
        </div>
        <div>
          <div
            class="tab"
            v-if="
              !!amenityInfo.length &&
              !amenityInfo.every((info) => !info.image_url)
            "
          >
            <div
              class="tab-slider"
              v-bind:style="{ display: loading ? 'none' : '' }"
            >
              <Carousel ref="slick" v-bind="sliderOptions">
                <Slide
                  v-for="amenityInfoImage in amenityInfo"
                  :key="amenityInfoImage.seq_position"
                >
                  <div>
                    <LazyImage
                      :src="addScaler(amenityInfoImage.image_url)"
                      :alt="amenityInfoImage.caption"
                      :isNeighborhoodImage="false"
                    />
                    <h4
                      class="ammenity-imginfo"
                      v-if="amenityInfoImage.caption"
                    >
                      {{ amenityInfoImage.caption }}
                    </h4>
                  </div>
                </Slide>
                <template #addons>
                  <div class="carousel-navigation" v-if="amenityInfo.length > 1">
                    <Navigation />
                  </div>
                </template>
              </Carousel>
            </div>
          </div>
          <div class="content-bottom" v-else-if="amenityCaption">
            {{ amenityCaption }}
          </div>
        </div>

        <div v-if="amenities.length > 1" class="ammenity-next">
          <span @click="loadNewAmenity()">
            Next feature:
            <span class="ammenity-next-link">
              {{ nextAmenityName }}
              <i class="ammenity-next-icon">
                <LeftArrowIcon class="rotate-180" />
              </i>
            </span>
          </span>
        </div>
      </div>
    </div>

    <!--bottom arrow-->
    <div
      v-if="bottomArrowCondition(`left`, `top`)"
      v-bind:style="{ top: `${lotPosition.arrowPosition}px` }"
      class="arrow-pointer arrow-top-left"
    ></div>
    <div
      v-if="bottomArrowCondition(`right`, `top`)"
      v-bind:style="{ top: `${lotPosition.arrowPosition}px` }"
      class="arrow-pointer arrow-top-right"
    ></div>
    <div
      v-if="bottomArrowCondition(`left`, `bottom`)"
      v-bind:style="{ top: `${lotPosition.arrowPosition}px` }"
      class="arrow-pointer arrow-bottom-left"
    ></div>
    <div
      v-if="bottomArrowCondition(`right`, `bottom`)"
      v-bind:style="{ top: `${lotPosition.arrowPosition}px` }"
      class="arrow-pointer arrow-bottom-right"
    ></div>
    <!-- <div
      v-if="hasMobile && bottomArrowCondition(`left`, `bottom`)"
      v-bind:style="[
        !lodash.isEmpty(lotPosition)
          ? {
              left: `${lotPosition.arrowPosition}px`,
            }
          : {},
      ]"
      class="arrow-pointer"
    ></div> -->
    <!--<div
      v-if="hasTablet"
      v-bind:style="[
        !lodash.isEmpty(lotPosition)
          ? {
              left: `${lotPosition.arrowPosition}px`,
            }
          : {},
      ]"
      class="arrow-pointer arrow-top"
    ></div> -->
  </div>
</template>

<style lang="scss" scoped>
.elem-modal {
  .ammenity {
    &-title {
      @include font(16px, 700);
      color: $primary;
      line-height: 16px;
      margin-bottom: 6px;
      width: calc(100% - var(--dsize, 25px));
      text-transform: capitalize;
    }

    &-site-address {
      @include font(10px, 600);
      line-height: 14px;
      color: $gray-900;
    }

    &-imginfo {
      color: $gray-600;
      @include font(14px, 500);
      margin-top: 10px;
    }

    &-next {
      @include font(9px, 600);
      line-height: 14px;
      text-align: right;
      color: $lime-green-new;
      position: relative;

      &-link {
        text-decoration: underline;
        margin-left: 3px;
        cursor: pointer;
        font-size: 12px;
      }

      &-icon {
        display: inline-block;
        vertical-align: -1px;

        svg {
          height: 10px;
        }
      }
    }
  }
}

.tab-slider img {
  cursor: default;
}
</style>
